import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, ApiError } from "@/lib/api";

const T = {
  ink: "#17131F",
  paper: "#FBFAFE",
  accent: "var(--accent)",
  slate: "#7C7590",
  border: "#E8E4DC",
  white: "#FFFFFF",
  danger: "#B91C1C",
  dangerBg: "#FEF2F2",
};

export function EraseConfirm() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [stage, setStage] = useState<"confirm" | "erasing" | "done">("confirm");
  const [error, setError] = useState("");

  async function handleErase() {
    setStage("erasing");
    setError("");
    try {
      const res = await api.post<{ ok: boolean; already_erased?: boolean; error?: string }>(
        `/invite/${token}/erase`,
        {}
      );
      if (res.ok || res.already_erased) {
        setStage("done");
      } else {
        setError(res.error ?? "Could not erase data.");
        setStage("confirm");
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        // Not authenticated — send to OTP verify then back
        navigate(`/invite/${token}/verify`, { replace: true });
        return;
      }
      setError("Something went wrong. Please try again.");
      setStage("confirm");
    }
  }

  return (
    <div
      style={{
        minHeight: "100dvh",
        background: T.paper,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 420,
          background: T.white,
          borderRadius: 16,
          boxShadow: "0 24px 64px rgba(27,26,36,0.10), 0 4px 12px rgba(27,26,36,0.06)",
          padding: "32px 28px",
          textAlign: "center",
        }}
      >
        {stage === "done" ? (
          <>
            <div style={{ fontSize: 40, marginBottom: 16 }}>✓</div>
            <h2 style={{ margin: "0 0 12px", fontSize: 22, fontWeight: 700, color: T.ink }}>
              Data removed
            </h2>
            <p style={{ margin: "0 0 24px", fontSize: 14, color: T.slate, lineHeight: 1.6 }}>
              The transcripts, scorecards, job description and notes for this interview have been permanently deleted, and so has your CV unless another active invite under the same email still uses it. This cannot be undone.
            </p>
            <button
              onClick={() => navigate("/candidate/home")}
              style={{
                padding: "11px 24px",
                fontSize: 14,
                fontWeight: 600,
                color: T.white,
                background: T.accent,
                border: "none",
                borderRadius: 8,
                cursor: "pointer",
              }}
            >
              Return to home
            </button>
          </>
        ) : (
          <>
            <div style={{ fontSize: 36, marginBottom: 16 }}>⚠️</div>
            <h2 style={{ margin: "0 0 12px", fontSize: 22, fontWeight: 700, color: T.ink }}>
              Delete your data?
            </h2>
            <p style={{ margin: "0 0 8px", fontSize: 14, color: T.slate, lineHeight: 1.6 }}>
              This will permanently delete the transcripts, scorecards, job description and notes for this interview, and your CV. If another active invite under the same email still uses your CV, it is kept until that invite is erased too.
            </p>
            <p style={{ margin: "0 0 24px", fontSize: 13.5, fontWeight: 600, color: T.danger }}>
              This cannot be undone.
            </p>

            {error && (
              <div
                style={{
                  marginBottom: 20,
                  padding: "12px 14px",
                  background: T.dangerBg,
                  borderRadius: 8,
                  border: `1px solid #FECACA`,
                }}
              >
                <p style={{ margin: 0, fontSize: 13, color: T.danger }}>{error}</p>
              </div>
            )}

            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button
                onClick={() => navigate(-1)}
                disabled={stage === "erasing"}
                style={{
                  padding: "11px 20px",
                  fontSize: 14,
                  fontWeight: 600,
                  color: T.slate,
                  background: "transparent",
                  border: `1px solid ${T.border}`,
                  borderRadius: 8,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleErase}
                disabled={stage === "erasing"}
                style={{
                  padding: "11px 20px",
                  fontSize: 14,
                  fontWeight: 600,
                  color: T.white,
                  background: stage === "erasing" ? T.slate : T.danger,
                  border: "none",
                  borderRadius: 8,
                  cursor: stage === "erasing" ? "default" : "pointer",
                  transition: "background 0.15s",
                }}
              >
                {stage === "erasing" ? "Deleting…" : "Yes, delete everything"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
